import qs from 'qs';
import * as R from 'ramda/src/index';
import moment from 'moment';
import {computed, action, extendObservable, decorate, toJS} from 'mobx';
import {brandLogo} from 'helpers/commonHelper';
import {buildFerryUrl, buildAddTripUrl, getPopularPorts} from 'affiliates/Widget/helper';
import Fuse from 'fuse.js/dist/fuse.common';
import {
  ONEWAY_TRIP_TYPE,
  ROUND_TRIP_TYPE,
  MULTI_TRIP_TYPE,
  getInvertType,
  findByCode
} from 'ferries/Home/components/FerrySearchMask/helperStore';
import {lowerCaseAllWordsExceptFirstLetter} from 'ferries/helpers/common';
import {
  dateIsSameOrAfterToday,
  obDateIsSameOrAfterTodayAndIbDateIsAfterObDate
} from 'helpers/time';
import {isFerryscanner} from 'helpers/stableValues';
import {onlyCharacters} from 'helpers/text';

class FerrySearchMaskStore {

  constructor({getPorts, brand, translations}) {
    const {enableMulti, outline, popular, joinProgram} = qs.parse(location.search.slice(1));

    extendObservable(this, {
      typeTrip: ROUND_TRIP_TYPE, // 0 oneway, 1 roundtrip, 2 multitrip
      triedToSubmit: false,
      trips: [this.newTrip()],
      errors: [],
      loading: false
    });

    Object.assign(this, {
      product: 'ferry',
      brandLogo: brandLogo(brand, 'away'),
      minTrips: 1,
      maxTrips: 10,
      originalPorts: [],
      enableMulti: enableMulti === 'true',
      joinProgram: joinProgram === 'true' && isFerryscanner,
      outline: outline === 'true',
      popular: popular === 'true',
      translations
    });

    getPorts({isBilingual: false}).then(this.setPorts);
  }

  fillWithQuery = ({dep, arr, obDate, ibDate, typeTrip}) => {
    const deps = dep ? dep.split(',') : [];
    const arrs = arr ? arr.split(',') : [];
    const obDates = obDate && !(obDate instanceof Date) ? obDate.split(',') : (obDate instanceof Date ? [obDate] : []);
    const ibDates = ibDate && !(ibDate instanceof Date) ? ibDate.split(',') : (ibDate instanceof Date ? [ibDate] : []);
    const nofTrips = deps.length || 1;
    const typeTripNumber = typeTrip ? parseInt(typeTrip ?? ONEWAY_TRIP_TYPE) : undefined;

    const currentTripsLength = this.noOfTrips;
    if (nofTrips > currentTripsLength) {
      for (let i = 0; i < nofTrips - currentTripsLength; i++) {
        this.addTrip();
      }
    } else if (nofTrips < currentTripsLength) {
      for (let i = currentTripsLength; i > nofTrips; i--) {
        this.removeTrip(i - 1);
      }
    }

    for (let tripIndex = 0; tripIndex < nofTrips; tripIndex++) {
      if (deps[tripIndex] && deps[tripIndex].length >= 3) {
        this.setPortByThreeLetterCode('dep', deps[tripIndex], tripIndex);
      }

      if (arrs[tripIndex] && arrs[tripIndex].length >= 3) {
        this.setPortByThreeLetterCode('arr', arrs[tripIndex], tripIndex);
      }

      if (!(obDates[tripIndex] instanceof Date)) {
        obDates[tripIndex] = new Date(obDates[tripIndex]);
      }

      if (!(ibDates[tripIndex] instanceof Date)) {
        ibDates[tripIndex] = new Date(ibDates[tripIndex]);
      }

      if (dateIsSameOrAfterToday(obDates[tripIndex])) {
        this.setDate('obDate', obDates[tripIndex], tripIndex);
      }

      if (obDateIsSameOrAfterTodayAndIbDateIsAfterObDate(obDates[tripIndex], ibDates[tripIndex])) {
        this.setDates({fromDate: obDates[tripIndex], toDate: ibDates[tripIndex]}, tripIndex);
      }
    }

    if ([ONEWAY_TRIP_TYPE, ROUND_TRIP_TYPE, MULTI_TRIP_TYPE].includes(typeTripNumber)) {
      this.setTypeTrip(typeTripNumber);
    }

  };

  portString = ({locationName, locationAbbr, locationCountry}) => {
    const portName = lowerCaseAllWordsExceptFirstLetter(locationName);
    return [`${portName}, ${locationCountry}`, `${portName} (${locationAbbr}), ${locationCountry}`];
  };

  portStringNo3Ltr = ({locationName, locationCountry}) => {
    const portName = lowerCaseAllWordsExceptFirstLetter(locationName);
    return `${portName}, ${locationCountry}`;
  };

  setPort = (type, value, tripIndex) => {
    const valueIsArray = Array.isArray(value);
    const val = valueIsArray ? value[0] : onlyCharacters(value);
    const valQuery = valueIsArray ? value[1] : onlyCharacters(value);

    this.trips[tripIndex][type] = val;
    this.trips[tripIndex][type + 'Query'] = valQuery;
    this.trips[tripIndex][type + 'Suggestion'] = {};

    if (this.errors.length > 0 && this.errors[tripIndex] && this.errors[tripIndex][type]) {
      this.errors[tripIndex][type] = [];
    }
  };

  setPorts = (ports) => {
    const transformedPorts = ports.map((port) => ({
      ...port,
      isMetropolitan: port.parentLocationID === null && port.childPorts.length > 0,
      alternativeNames: port.alternativeNames.split('|')
    }));

    this.originalPorts = R.sortBy(R.prop('locationName'), transformedPorts);

    this.trips.forEach((trip) => {
      trip.depPossiblePorts = this.originalPorts;
      trip.depSelected = this.originalPorts;
      trip.depPopularPorts = this.popular ? getPopularPorts(transformedPorts) : [];
    });

    const {search} = window.location;
    if (search) {
      const query = qs.parse(search.slice(1));
      this.fillWithQuery(query);
    }
  };

  swapPorts = (tripIndex) => {
    this.swapValues('dep', 'arr', tripIndex);
    this.swapValues('depQuery', 'arrQuery', tripIndex);
    this.swapValues('depSuggestion', 'arrSuggestion', tripIndex);
    this.swapValues('depSelected', 'arrSelected', tripIndex);
    this.swapValues('depPossiblePorts', 'arrPossiblePorts', tripIndex);
  };

  swapValues = (type1, type2, tripIndex) => {
    const type = this.trips[tripIndex][type1];
    this.trips[tripIndex][type1] = this.trips[tripIndex][type2];
    this.trips[tripIndex][type2] = type;
  };

  setPortByThreeLetterCode = (type, code, tripIndex) => {
    const port = findByCode(this.originalPorts, code);
    if (port) {
      this.setPort(type, this.portString(port), tripIndex);
    }
    this.onSuggestionSelected(type, port, tripIndex);
  };

  clearSuggestions = (type, tripIndex) => {
    const invertType = getInvertType(type);
    const emptyPortsValue = !(this.trips[tripIndex][type] || this.trips[tripIndex][invertType]);
    if (emptyPortsValue && type === 'dep') {
      this.trips[tripIndex][`${invertType}Selected`] = [];
      this.trips[tripIndex][`${invertType}PopularPorts`] = [];
    }
  };

  fetchSuggestions = (type, value, tripIndex) => {
    const val = onlyCharacters(value);
    const invertType = getInvertType(type);
    const isDeparture = type === 'dep';

    if (!val) {
      if (isDeparture) {
        this.trips[tripIndex][`${type}Selected`] = this.originalPorts;
      } else {
        if (!this.trips[tripIndex][`${invertType}Query`]) {
          this.trips[tripIndex][`${type}Selected`] = this.originalPorts;
        }
      }
    } else {
      const options = {
        threshold: 0.3,
        distance: 80,
        keys: [
          'locationAbbr',
          'locationName',
          'locationNameLatin',
          'alternativeNames'
        ]
      };

      const pattern = val.replace(/[-[\]{}()*+?,\\^$|#]/gi, '');
      const listOfPortsToSearch = isDeparture ? this.originalPorts : this.trips[tripIndex][`${type}PossiblePorts`];

      const fuse = new Fuse(toJS(listOfPortsToSearch), options);
      this.trips[tripIndex][`${type}Selected`] = fuse.search(pattern).map((item) => item.item);
      if (this.trips[tripIndex][`${type}Selected`].length === 0) {
        this.trips[tripIndex][`${type}Selected`] = listOfPortsToSearch;
      }
    }
  };

  findPorts = (type, tripIndex) => {
    const invertType = getInvertType(type);
    const trip = this.trips[tripIndex];
    const suggestion = trip[`${type}Suggestion`];
    const dependentPorts = (suggestion?.dependentPorts ?? [])
      .filter((port) => ![suggestion.parentLocationID, suggestion.ferryOwnLocationID].includes(port));

    const matchPorts = dependentPorts.reduce((acc, dependentPort) => {
      const foundPort = this.originalPorts.find(({ferryOwnLocationID}) => ferryOwnLocationID === dependentPort);
      if (foundPort) {
        acc.push(foundPort);
      }
      return acc;
    }, []);

    const portsSortedByLocationName = R.sortBy(R.prop('locationName'), matchPorts);

    trip[`${invertType}PossiblePorts`] = portsSortedByLocationName;
    trip[`${invertType}Selected`] = trip[`${invertType}PossiblePorts`];
    if (type === 'dep') {
      trip[`${invertType}PopularPorts`] = this.popular ? getPopularPorts(matchPorts) : [];
    }
  };

  onSuggestionSelected = (type, suggestion, tripIndex) => {
    this.trips[tripIndex][`${type}Suggestion`] = suggestion;

    if (type === 'arr' && tripIndex < this.noOfTrips - 1 && !this.trips[tripIndex + 1][getInvertType(type)]) {
      this.trips[tripIndex + 1][getInvertType(type)] = this.trips[tripIndex][type];
      this.trips[tripIndex + 1][getInvertType(type) + 'Query'] = this.trips[tripIndex][type + 'Query'];
      this.trips[tripIndex + 1][getInvertType(type) + 'Suggestion'] = this.trips[tripIndex][type + 'Suggestion'];
      this.findPorts(getInvertType(type), tripIndex + 1);
    }
    this.findPorts(type, tripIndex);
  };

  setDate = (type, date, tripIndex) => {
    this.trips[tripIndex][type] = date;
  };

  setDates = (date, tripIndex) => {
    const {fromDate, toDate} = date;
    this.setNextDates(tripIndex, fromDate);

    this.trips[tripIndex].obDate = fromDate;
    this.trips[tripIndex].ibDate = toDate;
  };

  setNextDates = (tripIndex, fromDate) => {
    let dateToAdd;
    let oldDate = this.trips[tripIndex].obDate;
    for (let i = tripIndex; i < this.noOfTrips; i++) {
      dateToAdd = i === tripIndex ? fromDate : this.trips[i].obDate;
      if (i + 1 < this.noOfTrips && this.trips[i + 1] && dateToAdd > this.trips[i + 1].obDate) {
        let diffInDays = moment(this.trips[i + 1].obDate).diff(oldDate, 'days');
        oldDate = this.trips[i + 1].obDate;
        this.trips[i + 1].obDate = moment(dateToAdd).add(diffInDays, 'days').toDate();
        this.trips[i + 1].ibDate = moment(dateToAdd).add(diffInDays, 'days').toDate();
      }
    }
  };

  setTypeTrip = (val) => {
    this.typeTrip = val;
  };

  setErrors = ({errors}) => {
    if (this.triedToSubmit && errors) {
      this.loading = false;
    }
    this.errors = errors ?? [];
  };

  newTrip = () => {
    const prevTrip = this.noOfTrips > 0 ? this.trips[this.noOfTrips - 1] : undefined;

    return {
      dep: '',
      depQuery: '',
      arr: '',
      arrQuery: '',
      depSuggestion: {},
      arrSuggestion: {},
      depSelected: [],
      arrSelected: [],
      depPossiblePorts: [],
      arrPossiblePorts: [],
      depPopularPorts: [],
      arrPopularPorts: [],
      obDate: prevTrip ? moment(prevTrip.obDate).add(3, 'days').toDate() : moment().add(20, 'days').toDate(),
      ibDate: prevTrip ? moment(prevTrip.ibDate).add(3, 'days').toDate() : moment().add(25, 'days').toDate()
    };
  };

  addTrip = () => {
    if (this.enableMulti) {
      if (this.noOfTrips < this.maxTrips) {
        this.trips.push(this.newTrip());
        this.trips[this.noOfTrips - 1].depSelected = [];
        this.trips[this.noOfTrips - 1].arrSelected = [];
        this.trips[this.noOfTrips - 1].depPopularPorts = [];
        this.trips[this.noOfTrips - 1].arrPopularPorts = [];
        this.trips[this.noOfTrips - 1].depPossiblePorts = this.originalPorts;
        this.trips[this.noOfTrips - 1].arrPossiblePorts = this.originalPorts;

        if (this.trips[this.noOfTrips - 2].arr) {
          this.trips[this.noOfTrips - 1].dep = this.trips[this.noOfTrips - 2].arr;
          this.trips[this.noOfTrips - 1].depQuery = this.trips[this.noOfTrips - 2].arrQuery;
          this.trips[this.noOfTrips - 1].depSuggestion = this.trips[this.noOfTrips - 2].arrSuggestion;
          this.findPorts('dep', this.noOfTrips - 1);
        }
      }
    } else {
      const {routes} = this;
      window.open(buildAddTripUrl({...routes[0]}));
    }
  };

  removeTrip = (index) => {
    if (this.noOfTrips > this.minTrips) {
      this.trips.splice(index, 1);
    }
  };

  submitForm = (event) => {
    event.preventDefault();
    this.triedToSubmit = true;
    this.errors = [];
    const {typeTrip, routes, setErrors} = this;
    buildFerryUrl({
      typeTrip,
      trips: routes
    })
      .then((url) => {
        window.open(url);
      })
      .catch(setErrors);
  };

  get noOfTrips() {
    return this.trips?.length;
  }

  get routes() {
    const {typeTrip, trips} = this;
    return trips.reduce((acc, {depQuery, depSuggestion, arrQuery, arrSuggestion, obDate, ibDate}) => {

      if ([ONEWAY_TRIP_TYPE, ROUND_TRIP_TYPE].includes(typeTrip) && acc.length === 0) {
        acc.push({
          dep: depQuery,
          arr: arrQuery,
          depSuggestion,
          arrSuggestion,
          obDate,
          ibDate
        });

        if (typeTrip === ROUND_TRIP_TYPE) {
          acc.push({
            dep: arrQuery,
            arr: depQuery,
            depSuggestion,
            arrSuggestion,
            obDate: ibDate,
            ibDate
          });
        }
      } else if (typeTrip === MULTI_TRIP_TYPE) {
        acc.push({
          dep: depQuery,
          arr: arrQuery,
          depSuggestion,
          arrSuggestion,
          obDate: obDate,
          ibDate
        });
      }

      return acc;
    }, []);
  }
}

decorate(FerrySearchMaskStore, {
  findPorts: action,
  fillWithQuery: action,
  setPort: action,
  setPorts: action,
  swapValues: action,
  setPortByThreeLetterCode: action,
  clearSuggestions: action,
  fetchSuggestions: action,
  onSuggestionSelected: action,
  setDate: action,
  setDates: action,
  setTypeTrip: action,
  onChangeTab: action,
  setErrors: action,
  newTrip: action,
  addTrip: action,
  removeTrip: action,
  setNextDates: action,
  submitForm: action,
  noOfTrips: computed,
  routes: computed
});

export default FerrySearchMaskStore;
